import App from "+/Engine/App";
import Base from "+/Engine/Base";
import EntityReference from "+/Engine/EntityReference";
import resolveActionParams from "+/Engine/Helpers/resolveActionParams";
import _ from "lodash";

export default class Entity extends Base {

  // ----------- //
  // Static Vars //
  // ----------- //

  static #idCounter = 0;
  static #lastGeneratedId = 0;
  static #standardFields = {
    "status": {
      "type": "enum",
      "enumOptions": [
        "active",
        "inactive",
      ],
      "default": "active",
    },
    "timestampCreated": {
      "type": "int",
      "immutable": true,
      "onCreate": "now",
    },
    "timestampUpdated": {
      "type": "int",
      "onUpdate": "now",
    },
  };

  static #validFieldTypes = [
    "int",
    "string",
    "boolean",
    "enum",
    "story",
    "email",
    "image",
    "oneEntity",
    "manyEntities",
  ];

  static #manyEntitiesValidKeys = [
    "type",
    "stateOnly",
    "entity",
    "remoteLinkingField",
    // Matches the server's manyEntities support (BaseEntity.php:1652).
    // Filtering happens server-side in the SQL WHERE clause —
    // client just needs to accept the key in App.json validation.
    "remoteCurrentUserField",
    "additionalLinkingCriteria",
    "initQty",
    "shallow",
    "offsets",
    "startAtEnd",
    "limit",
    "trackLastSeen",
  ];

  static #oneEntityValidKeys = [
    "type",
    "stateOnly",
    "entity",
    "remoteLinkingField",
    "remoteCurrentUserField",
    "default",
  ];

  static #primativeValidKeys = [
    "type",
    "generated",
    "default",
    "stateOnly",
    "enumOptions",
    "immutable",
    "onCreate",
    "onUpdate",
    "primaryKey",
    "inputType",
    "exposeAsInput",
  ];

  // --------------------- //
  // Public Static Methods //
  // --------------------- //

  static makeTemp(appName, entityConfig, fields) {
    const pk = this.#generateTempPk(entityConfig);

    Object.keys(entityConfig.fields).forEach(fieldName => {
      if (entityConfig.fields[fieldName].onCreate === "now") {
        if (!fields[fieldName]) {
          fields[fieldName] = Math.floor(Date.now() / 1000);
        }
      }

      if (entityConfig.fields[fieldName].onUpdate === "now") {
        if (!fields[fieldName]) {
          fields[fieldName] = Math.floor(Date.now() / 1000);
        }
      }
    });

    const instance = new Entity(appName, entityConfig, pk, true);
    instance.hydrateFields(fields, false);
    return instance;
  }

  static mergeStandardFieldsWithEntityFields(entityFields) {
    const fieldDefinitions = {};
    Object.keys(entityFields).forEach(fieldName => {
      fieldDefinitions[fieldName] = entityFields[fieldName];
    });
    Object.keys(Entity.#standardFields).forEach(fieldName => {
      if (!fieldDefinitions[fieldName]) {
        fieldDefinitions[fieldName] = Entity.#standardFields[fieldName];
      }
    });
    return fieldDefinitions;
  }

  // Resolves an eventParams definition map into a concrete param object.
  // contexts is the caller-supplied context object used by "context" and "contextField" sources.
  extractEventParamsForAction(actionParamsDef, eventName, contexts) {
    const resolved = resolveActionParams(actionParamsDef, contexts, this);

    // Unwrap oneEntity context params to their pk — this is event-specific knowledge
    // that doesn't belong in the generic resolver.
    const eventDef = App.get(this.#appName).getAppEvents()[eventName];
    if (eventDef) {
      for (const [paramName, value] of Object.entries(resolved)) {
        if (eventDef.params?.[paramName]?.type === "oneEntity" && value?.pk) {
          resolved[paramName] = value.pk;
        }
      }
    }

    return resolved;
  }

  // ------------- //
  // Instance Vars //
  // ------------- //

  #appName;
  #pk;
  #tempPk = {};
  #isPkTemp = false;

  #type;
  #primaryKeyFields = [];
  #originalFieldDefinitions = {};
  #actionDefinitions = {};

  #initialized = false;
  #initializing = false;
  #isLoading;
  #isHydrated = false;

  #fieldDefinitions = {};
  #myFieldData = {};
  #fieldTypes = {};

  // ----------------------- //
  // Public Instance Methods //
  // ----------------------- //

  get fieldDefinitions() {
    return this.#fieldDefinitions;
  }

  toJson() {
    return {
      // start constructor values //
      appName: this.#appName,

      // start destructered config //
      type: this.#type,
      // primaryKeyFields: this.#primaryKeyFields,
      // originalFieldDefinitions: this.#originalFieldDefinitions,
      // actionDefinitions: this.#actionDefinitions,
      // end destructered config //

      // pk: this.#pk,
      isPkTemp: this.#isPkTemp,
      // end constructor values //

      // start state vars //
      initialized: this.#initialized,
      initializing: this.#initializing,
      isLoading: this.#isLoading,
      isHydrated: this.#isHydrated,
      fieldData: this.#myFieldData,
      // end state vars //
    };
  }

  static fromJson(json) {
    const app = App.get(json.appName);
    const config = app.getEntityConfig(json.type);
    const pk = {};
    config.primaryKeyFields.forEach(pkFieldName => pk[pkFieldName] = json.fieldData[pkFieldName]);
    const me = new Entity(json.appName, config, pk, json.isPkTemp);
    me.#instanceFromJson(json);
    return me;
  }

  #instanceFromJson(json) {
    this.#initialized = json.initialized;
    this.#initializing = json.initializing;
    this.#isLoading = json.isLoading;
    this.#isHydrated = json.isHydrated;
    this.#myFieldData = json.fieldData;
    // this.myFieldData = json.myFieldData;
  }

  constructor(appName, entityConfig, pk, isPkTemporary = false) {
    super();
    this.#assertValidClass();
    Entity.#assertValidEntityDef(entityConfig);

    this.#appName = appName;
    //    this.#config = structuredClone(entityConfig);
    this.#pk = pk;
    this.#isPkTemp = isPkTemporary;

    this.#type = entityConfig.entityType;
    this.#primaryKeyFields = _.cloneDeep(entityConfig.primaryKeyFields);
    this.#primaryKeyFields.forEach(fieldName => {
      this.#myFieldData[fieldName] = pk[fieldName];
      //      this.myFieldData[fieldName] = pk[fieldName];
    });
    this.#actionDefinitions = _.cloneDeep(entityConfig.actions);
    this.#originalFieldDefinitions = _.cloneDeep(entityConfig.fields);
    if (entityConfig.ignoresStandardFields) {
      this.#fieldDefinitions = entityConfig.fields;
    } else {
      this.#fieldDefinitions = this.constructor.mergeStandardFieldsWithEntityFields(entityConfig.fields);
    }
    Object.keys(this.#fieldDefinitions).forEach(fieldName => {
      this.#fieldTypes[fieldName] = this.#fieldDefinitions[fieldName].type;
      // manyEntities values live exclusively in FeedStore — never on the entity.
      // Skip the null init so #myFieldData has no key for them at all.
      // See README.md "Invariant: entities never store values for their manyEntities fields".
      const isManyEntitiesField = this.#fieldDefinitions[fieldName].type === "manyEntities";
      if (!this.#primaryKeyFields.includes(fieldName) && !isManyEntitiesField) {
        if (this.#isPkTemp && this.#fieldDefinitions[fieldName].default) {
          this.#myFieldData[fieldName] = this.#fieldDefinitions[fieldName].default;
        } else {
          this.#myFieldData[fieldName] = null;
        }
        //        this.myFieldData[fieldName] = this.#fieldDefinitions[fieldName].default ?? null;
      }
      Object.defineProperty(this, fieldName, {
        get: () => {
          switch (this.#fieldDefinitions[fieldName].type) {
            case "manyEntities": {
              const app = App.get(this.#appName);
              const feedStore = app.getFeedStore(this.#type, this.getPk(), fieldName);
              return feedStore.items
                  .map(item => app.getEntity(item.entityType, item.entityPk))
                  .filter(Boolean);
            }
            default:
              return this.#myFieldData[fieldName];
          }
        },
        enumerable: true,
      });
    });
  }

  // Actions are the ONE place allowed to hold a `contexts` map: they resolve the event's
  // params from it (via extractEventParamsForAction → resolveActionParams) and then drop it.
  // Context is never handed to the event itself — events and their routines run on event
  // params alone. Session-scoped state (e.g. the current user) is reached from routines via
  // `source: currentUser`, which reads the active session, not any caller-supplied map.
  // Returns the triggerEvent result — the param dictionary populated
  // with each routine's output. Callers that need to read a created
  // entity's temp pk (e.g. to await its real-id reconciliation) use
  // this; everywhere else the return value is ignored.
  triggerAction(actionName, contexts) {
    const eventName = this.#getActionEventName(actionName);
    const eventParams = this.#getActionEventParams(actionName, contexts);
    return App.get(this.#appName).triggerEvent(eventName, eventParams);
  }

  #getActionEventName(actionName) {
    if (!this.#actionDefinitions[actionName]) {
      throw new Error(`entity ${this.#type} does not have an action named "${actionName}"`);
    }
    return this.#actionDefinitions[actionName].eventName;
  }

  #getActionEventParams(actionName, contexts) {
    const actionDefinition = this.#actionDefinitions[actionName];
    return this.extractEventParamsForAction(actionDefinition.eventParams, actionDefinition.eventName, contexts);
  }

  getTransformedValue(paramDef) {
    // TODO: confirm if this is the correct approach - does transform option make sense in JSON structure for params
    const value = this.#myFieldData[paramDef.fieldName];

    switch (paramDef.transform) {
      case "not":
        return !value;
      default:
        return value;
    }
  }

  getType() {
    return this.#type;
  }

  getPk() {
    const pk = {};
    this.#primaryKeyFields.forEach(fieldName => {
      pk[fieldName] = this.#myFieldData[fieldName];
    });
    return pk;
  }

  isEntityPkTemp() {
    return this.#isPkTemp;
  }

  getTempPkString() {
    return App.getPkString(this.#tempPk);
  }

  // Returns the temp-PK fields keyed by PK field name. Empty
  // object on entities loaded from the server (no client-supplied
  // temp PK to reconcile against). The App-level rewrite uses
  // this to record `tempValue → realValue` pairs once the server
  // returns the real PK so any transactions queued against the
  // temp PK (e.g. UpdateEntity fired before the create's response
  // arrived) get rewritten before the next sync flush.
  getTempPk() {
    return { ...this.#tempPk };
  }

  get(key) {
    if (!(key in this)) {
      return this._define(key);
    }
    return this[key];
  }

  getBasicFieldData() {
    const basicData = {};
    Object.keys(this.#fieldDefinitions).forEach(fieldName => {
      // manyEntities values live exclusively in FeedStore — never mirrored onto the entity.
      // See README.md "Invariant: entities never store values for their manyEntities fields".
      if (this.#fieldDefinitions[fieldName].type === "manyEntities") {
        return;
      }
      basicData[fieldName] = this.#myFieldData[fieldName];
    });
    return basicData;
  }

  getFieldData() {
    return this.#myFieldData;
  }

  isLoading() {
    return this.#isLoading;
  }

  getFieldType(fieldName) {
    return this.#fieldTypes[fieldName];
  }

  hydrateFields(fields, skipStateOnlyFields) {
    // @todo consider skipping primaryKeyFields or exceptioning if they are provided
    Object.keys(fields).forEach(fieldName => {
      if (fieldName.startsWith("temp_") && this.#primaryKeyFields.includes(fieldName.slice(5))) {
        this.#tempPk[fieldName.slice(5)] = fields[fieldName];
        return;
      }

      if (!this.#fieldDefinitions[fieldName]) {
        throw new Error(`Cannot hydrate unknown field: '${fieldName}' for entity: '${this.#type}'`);
      }

      if (skipStateOnlyFields && this.#fieldDefinitions[fieldName].stateOnly) {
        return;
      }

      // manyEntities values live exclusively in FeedStore — never on the entity.
      // See README.md "Invariant: entities never store values for their manyEntities fields".
      if (this.#fieldDefinitions[fieldName].type === "manyEntities") {
        return;
      }

      let val;
      switch (this.#fieldDefinitions[fieldName].type) {
        case "oneEntity":
          if (this.#fieldDefinitions[fieldName].remoteLinkingField) {
            // @todo we don't hydrate any local pk?
            // maybe we store something though? probably not -- handle on fetch
          }
          /* eslint-disable */
          const remoteEntityType = this.#fieldDefinitions[fieldName].entity;
          let pk = {}
          const app = App.get(this.#appName);
          /* eslint-enable */
          app.getEntityPrimaryKeyFields(remoteEntityType).forEach(pkFieldName => {
            if (fields[fieldName] && fields[fieldName].pk) {
              pk = fields[fieldName].pk;
            } else if (fields[fieldName] != null && fields[fieldName][pkFieldName] != null) {
              // Defined-not-null, not truthy: 0 is a valid PK value (the Studio's
              // current-user Owner has id=0) and a truthy check silently drops it.
              pk[pkFieldName] = fields[fieldName][pkFieldName];
            }
          });
          if (Object.keys(pk).length > 0) {
            val = new EntityReference(remoteEntityType, pk);
          } else {
            val = {};
          }
          break;
        case "int":
          val = parseInt(fields[fieldName], 10);
          break;
        default:
          val = fields[fieldName];
          break;
      }
      this.#myFieldData[fieldName] = val;
    });
    this.#isHydrated = true;
  }

  init(app) {
    this.#initialized = false;
    this.#initializing = true;
    if (this.#needsMore()) {
      this.#requestInitFeeds(app);
    }
    this.#initialized = true;
    this.#initializing = false;
  }

  // ---------------------- //
  // Private Static Methods //
  // ---------------------- //

  static #generateTempPk(entityConfig) {
    const tempPk = {};
    entityConfig.primaryKeyFields.forEach(fieldName => {
      if (!entityConfig.fields[fieldName]) {
        throw new Error("entity has pkField that is missing from fields, this should have been validated already");
      }
      if (!entityConfig.fields[fieldName].generated) {
        throw new Error("cannot generate temp PK for field that is not defined as generated=true");
      }
      switch (entityConfig.fields[fieldName].type) {
        case "int": {
          const timestamp = Date.now();
          Entity.#idCounter += 1;
          const counter = Entity.#idCounter;

          tempPk[fieldName] = Math.max(timestamp, Entity.#lastGeneratedId + counter);
          if (Entity.#lastGeneratedId + counter < timestamp) {
            Entity.#idCounter = 0;
          }
          Entity.#lastGeneratedId = tempPk[fieldName];
          break;
        }
        default:
          throw new Error("cannot generate tempPk for unsupported field type");
      }
    });
    return tempPk;
  }

  static #isComposedOneEntityPkField(pkFieldName, fields) {
    for (const [fieldName, fieldDef] of Object.entries(fields)) {
      if (fieldDef.type !== "oneEntity" || !fieldDef.entity) {
        continue;
      }
      const prefix = `${fieldName}-${fieldDef.entity}-`.toLowerCase();
      if (pkFieldName.toLowerCase().startsWith(prefix)) {
        return true;
      }
    }
    return false;
  }

  static #assertValidEntityDef(config) {
    // validate type
    if (!config.entityType) {
      throw new Error("entity definitions must include an entityType");
    }
    if (typeof config.entityType !== "string" || config.entityType instanceof String) {
      throw new Error(`entity "${config.entityType}" key entityType must be a primative string`);
    }
    // validate pk fields
    if (!config.primaryKeyFields) {
      throw new Error(`entity "${config.entityType}" must define primaryKeyFields`);
    }
    if (!Array.isArray(config.primaryKeyFields)) {
      throw new Error(`entity "${config.entityType}" primaryKeyFields must be an array`);
    }
    config.primaryKeyFields.forEach(pkFieldName => {
      if (Object.keys(config.fields).includes(pkFieldName)) {
        return;
      }
      // Composed PK column names (pattern `<localField>-<remoteType>-<remotePkField>`) are
      // generated from an existing oneEntity field and do not appear as an explicit entry in
      // `fields`. Accept them when the prefix resolves to a declared oneEntity field whose
      // remote type matches. Mirrors BaseEntity::getLocalFieldNameForOneEntityPkField on PHP.
      if (Entity.#isComposedOneEntityPkField(pkFieldName, config.fields)) {
        return;
      }
      throw new Error(`entity "${config.entityType}" primaryKeyField ${pkFieldName} is not defined in fields`);
    });
    // validate fields
    if (!config.fields) {
      throw new Error(`entity "${config.entityType}" must define fields`);
    }
    if (config.fields === null || typeof config.fields !== "object" || Array.isArray(config.fields)) {
      throw new Error(`entity "${config.entityType}" fields must be an object`);
    }
    Object.keys(config.fields).forEach(fieldName => {
      this.#assertValidFieldDef(config.entityType, fieldName, config.fields[fieldName]);
    });
    // validate actions
    if (!config.actions) {
      throw new Error(`entity "${config.entityType}" must define actions`);
    }
    if (config.actions === null || typeof config.actions !== "object" || Array.isArray(config.actions)) {
      throw new Error(`entity "${config.entityType}" actions must be an object`);
    }
    Object.keys(config.actions).forEach(actionName => {
      this.#assertValidActionDef(config.entityType, actionName, config.actions[actionName]);
    });
  }

  static #assertValidFieldDef(entityType, fieldName, fieldDef) {
    if (!fieldDef.type) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" must define a type`);
    }
    if (!this.#validFieldTypes.includes(fieldDef.type)) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" define an unknown type ${fieldDef.type}`);
    }
    if (fieldDef.generated && typeof fieldDef.generated !== "boolean") {
      throw new Error(`entity ${entityType}'s field "${fieldName}" has a key "generated" but value is not a boolean`);
    }
    if (fieldDef.stateOnly && typeof fieldDef.stateOnly !== "boolean") {
      throw new Error(`entity ${entityType}'s field "${fieldName}" has a key "stateOnly" but value is not a boolean`);
    }
    switch (fieldDef.type) {
      case "oneEntity":
        this.#assertValidFieldDefTypeOneEntity(entityType, fieldName, fieldDef);
        break;
      case "manyEntities":
        this.#assertValidFieldDefTypeManyEntities(entityType, fieldName, fieldDef);
        break;
      default:
        this.#assertValidFieldDefTypePrimative(entityType, fieldName, fieldDef);
        break;
    }
  }

  static #assertValidFieldDefTypeOneEntity(entityType, fieldName, fieldDef) {
    if (!fieldDef.entity) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" is type oneEntity and must define which entity`);
    }
    // @todo permit and enforce proper use of remoteLinkingField and remoteCurrentUserField
    // @todo ask app to verify that this is a known entity
    Object.keys(fieldDef).filter(key => !this.#oneEntityValidKeys.includes(key)).forEach(extraneousKey => {
      throw new Error(`entity ${entityType}'s field "${fieldName}" definition contains an extraneous key "${extraneousKey}"`);
    });
  }

  static #assertValidFieldDefTypeManyEntities(entityType, fieldName, fieldDef) {
    if (!fieldDef.entity) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" is type manyEntities and must define which entity`);
    }
    // @todo require proper use of remoteLinkingField
    // @todo permit and enforce proper use of remoteCurrentUserField
    // @todo ask app to verify that this is a known entity
    Object.keys(fieldDef).filter(key => !this.#manyEntitiesValidKeys.includes(key)).forEach(extraneousKey => {
      throw new Error(`entity ${entityType}'s field "${fieldName}" definition contains an extraneous key "${extraneousKey}"`);
    });
    if (!Object.hasOwn(fieldDef, "initQty")) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" is type manyEntities but missing an initQty`);
    }
    if (!Number.isInteger(fieldDef.initQty)) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" initQty is not a number`);
    }
    if (!fieldDef.remoteLinkingField) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" is type manyEntities but missing a remoteLinkingField`);
    }
    // @todo ask app to verify that remoteLinkingField is valid
    if (!fieldDef.offsets) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" is type manyEntities but missing offsets`);
    }
    if (!Array.isArray(fieldDef.offsets)) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" offsets must be an array`);
    }
    fieldDef.offsets.forEach(offset => this.#assertValidManyEntitiesOffset(entityType, fieldName, offset));
  }

  static #assertValidManyEntitiesOffset(entityType, fieldName, offset) {
    if (!offset.name) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" offset missing name`);
    }
    if (!offset.field) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" offset missing field`);
    }
    // @todo verify field is valid with App
    if (!offset.direction) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" offset missing direction`);
    }
    if (!["asc", "desc"].includes(offset.direction)) {
      throw new Error(`entity ${entityType}'s field "${fieldName}" unsupported offset value: "${offset.direction}"`);
    }
  }

  static #assertValidFieldDefTypePrimative(entityType, fieldName, fieldDef) {
    Object.keys(fieldDef).filter(key => !this.#primativeValidKeys.includes(key)).forEach(extraneousKey => {
      throw new Error(`entity ${entityType}'s field "${fieldName}" definition contains an extraneous key "${extraneousKey}"`);
    });
  }

  static #assertValidActionDef(entityType, actionName, actionDef) {
    if (!actionDef.eventName) {
      throw new Error(`entity ${entityType}'s action "${actionName}" missing eventName`);
    }
    if (!actionDef.eventParams) {
      throw new Error(`entity ${entityType}'s action "${actionName}" missing eventParams`);
    }
    if (actionDef.eventParams === null || typeof actionDef.eventParams !== "object" || Array.isArray(actionDef.eventParams)) {
      if (Array.isArray(actionDef.eventParams)) {
        actionDef.eventParams = {}; // TODO: FIX backend returning empty object as array
      } else {
        throw new Error(`entity ${entityType}'s action "${actionName}" eventParams must be defined as an object`);
      }
    }
    Object.keys(actionDef.eventParams).forEach(paramName => {
      this.#assertValidActionEventParamDef(actionDef.eventParams[paramName]);
    });
  }

  static #assertValidActionEventParamDef(paramDef) {
    if (paramDef === null || typeof paramDef !== "object" || Array.isArray(paramDef)) {
      throw new Error("Entity action event params must be defined as objects");
    }
    if (!paramDef["source"]) {
      throw new Error("Entity action event params must define a source");
    }
    switch (paramDef.source) {
      case "context":
        if (!paramDef["context"]) {
          throw new Error("Entity action event params having source=context must define a context");
        }
        break;
      case "contextEntity":
        if (!paramDef["context"]) {
          throw new Error("Entity action event params having source=contextEntity must define a context");
        }
        if (!paramDef["fieldName"]) {
          throw new Error("Entity action event params having source=contextEntity must define a fieldName");
        }
        break;
      case "currentEntity":
        // no validation required
        break;
      case "currentEntityField":
        if (!paramDef["fieldName"]) {
          throw new Error("Entity action event params having source=currentEntityField must define a fieldName");
        }
        break;
    }
  }

  // ------------------------ //
  // Private Instance Methods //
  // ------------------------ //

  #assertValidClass() {
    if (!this.#primaryKeyFields) {
      throw new Error(`Entity "${this.constructor.name}" fails to define required primaryKeyFields`);
    }
  }

  #needsMore() {
    if (this.#initialized) {
      return false;
    }
    return Object.values(this.#fieldDefinitions).some(
      fieldDef => fieldDef.type === "manyEntities" && fieldDef.initQty !== 0,
    );
  }

  #requestInitFeeds(app) {
    if (this.#isPkTemp) {
      return;
    }
    const resolvedApp = app ?? App.get(this.#appName);
    // Skip the auto-trigger when this entity arrived as part of a
    // server-response merge. The server's `expandInitQtyFeeds` has
    // already preloaded every initQty != 0 feed for the entities
    // in the response, so triggering again here just queues
    // redundant fetches that flush on the next polling tick as a
    // multi-MB /feeds POST returning 0 bytes per requested feed —
    // and stalls the main thread for the duration of the merge.
    // `App.isMergingResponse()` is true exactly for the duration
    // of `App.mergeResponsePayload` (see App.js for why).
    if (resolvedApp.isMergingResponse?.()) {
      return;
    }
    Object.entries(this.#fieldDefinitions).forEach(([fieldName, fieldDef]) => {
      if (fieldDef.type !== "manyEntities" || fieldDef.initQty === 0) {
        return;
      }
      const feedStore = resolvedApp.getFeedStore(this.#type, this.getPk(), fieldName);
      feedStore.triggerInitialLoad();
    });
  }
}
